import { patchState, signalStoreFeature, type, withMethods, withState } from '@ngrx/signals'

import { Observable } from 'rxjs'
import { tap } from 'rxjs/operators'

import { ORGANIZATION_ADMIN_ROLE_ID } from '@flowx/designer/modules/workspaces/shared/commons.types'

import { PermissionValue, RoleAssetType, RoleDefinition } from '../../roles'
import { AccessManagementStoreDeps } from '../access-management-store.types'

// The return type of signalStoreFeature is an inferred generic that must not be widened.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function withRolesFeature() {
  return signalStoreFeature(
    { props: type<AccessManagementStoreDeps>() },
    withState({ availableRoles: [] as RoleDefinition[] }),
    withMethods((store) => ({
      /** Fetches the roles once; `availableRoles` is the reactive read surface. */
      loadRoles(): void {
        if (store.availableRoles().length) {
          return
        }

        store._rolesApi
          .getRoles()
          .pipe(
            tap((roles) =>
              patchState(store, {
                availableRoles: roles.filter((role) => role.id !== ORGANIZATION_ADMIN_ROLE_ID),
              })
            )
          )
          .subscribe()
      },
      addRole(role: RoleDefinition): Observable<RoleDefinition> {
        return store._rolesApi
          .createRole(role)
          .pipe(
            tap((newRole) =>
              patchState(store, { availableRoles: [...store.availableRoles(), newRole] })
            )
          )
      },
      updateRoleBasicInfo(role: RoleDefinition): Observable<RoleDefinition> {
        return store._rolesApi.updateRoleBasicInfo(role).pipe(
          tap((updatedRole) =>
            patchState(store, {
              availableRoles: store
                .availableRoles()
                .map((availableRole) =>
                  availableRole.id === updatedRole.id ? updatedRole : availableRole
                ),
            })
          )
        )
      },
      getRoleById(roleId: RoleDefinition['id']): Observable<RoleDefinition> {
        return store._rolesApi.getRoleById(roleId)
      },
      getRolePermissions(roleId: string): Observable<PermissionValue[]> {
        return store._rolesApi.getRolePermissions(roleId)
      },
      updateRolePermissions(roleId: string, permissions: PermissionValue[]): Observable<void> {
        return store._rolesApi.updateRolePermissions(roleId, { permissions })
      },
      getRolesByAssetType(assetType: RoleAssetType): Observable<RoleDefinition[]> {
        return store._rolesApi.getRolesByAssetType(assetType)
      },
    }))
  )
}
